import React from "react"
import { Close, Info } from "@components/icons"
import { IconWrapper } from "@components/icons/utils"
import VerticalSpace from "@components/VerticalSpace"
import {
  getCarsMakeModelYearUrl,
  getCarsMakeModelYearTrimUrl,
} from "@utils/urls"
import { useRouter } from "next/router"
import SelectInput from "@components/inputs/SelectInput"
import { useVehicleSelectionHandlers } from "@components/NavbarHeader/components/MenuDesktop/hooks/useVehicleSelectionHandlers"
import {
  Container,
  Overlay,
  CloseButton,
  Header,
  Body,
  Content,
  Filters,
  Filter,
  Label,
  Footer,
} from "./sharedStyles"
import { PowersportsCarSelectorProps } from "./types"

const PowersportsCarSelector: React.FC<PowersportsCarSelectorProps> = ({
  isOpen,
  cars,
  makeModelOrder = {},
  onRequestClose,
  onRequestCloseMenu,
}) => {
  const router = useRouter()

  // Filter to only powersports cars
  const filteredCars = cars.filter((car) => car.module === "powersports")

  const [make, setMake] = React.useState<string>("")
  const [model, setModel] = React.useState<string>("")
  const [trim, setTrim] = React.useState<string>("")
  const [year, setYear] = React.useState<string>("")
  const [models, setModels] = React.useState<Array<any>>([])
  const [trims, setTrims] = React.useState<Array<any>>([])
  const [years, setYears] = React.useState<Array<any>>([])

  // Use the shared vehicle selection handlers hook
  const {
    makes,
    onSelectMake: onSelectMakeBase,
    onSelectModel: onSelectModelBase,
    onSelectTrim: onSelectTrimBase,
  } = useVehicleSelectionHandlers({
    cars: filteredCars,
    selectedMake: make,
    selectedModel: model,
    selectedTrim: trim,
    setMake,
    setModel,
    setTrim,
    setYear,
    setModels,
    setTrims,
    setYears,
    isModern: false,
  })

  // Wrap handlers to add focus behavior specific to Powersports
  const onSelectMake = (makeValue: string) => {
    onSelectMakeBase(makeValue)
    setTimeout(() => {
      document.getElementById("Model")?.focus()
    }, 0)
  }

  const onSelectModel = (modelValue: string) => {
    onSelectModelBase(modelValue)
  }

  const onSelectTrim = (trimValue: string) => {
    onSelectTrimBase(trimValue)
    setTimeout(() => {
      document.getElementById("Year")?.focus()
    }, 0)
  }

  // Handle focus after model selection - focus Trim if trims are shown, otherwise Year
  React.useEffect(() => {
    if (model && trims.length > 0 && !trim) {
      setTimeout(() => {
        document.getElementById("Trim")?.focus()
      }, 0)
    } else if (model && trims.length === 0 && !year) {
      setTimeout(() => {
        document.getElementById("Year")?.focus()
      }, 0)
    }
  }, [model, trims.length, trim, year])

  const setCar = () => {
    if (onRequestCloseMenu) {
      onRequestCloseMenu()
    }
    window.scrollTo({ top: 20, behavior: "smooth" })

    if (trim) {
      router.push(getCarsMakeModelYearTrimUrl(make, model, year, trim))
    } else {
      router.push(getCarsMakeModelYearUrl(make, model, year))
    }
  }

  const reset = () => {
    setMake("")
    setModel("")
    setTrim("")
    setYear("")
    setModels([])
    setTrims([])
    setYears([])
  }

  React.useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen])

  return (
    <>
      <Overlay isVisible={isOpen} />
      <Container isOpen={isOpen}>
        <Header>
          <VerticalSpace height={30} />
          <CloseButton onClick={onRequestClose}>
            <IconWrapper>
              <Close />
            </IconWrapper>
          </CloseButton>
        </Header>
        <Body>
          <Label>
            <div>Shop by vehicle</div>
            <h1>Let's start that project</h1>
          </Label>
          <Content>
            <Filters>
              <Filter>
                <SelectInput
                  name="Make"
                  label="Make"
                  value={make}
                  options={makes}
                  onChange={(e) => {
                    onSelectMake(e.target.value)
                  }}
                />
              </Filter>
              <Filter disabled={!make}>
                <SelectInput
                  name="Model"
                  label="Model"
                  value={model}
                  options={models}
                  onChange={(e) => {
                    onSelectModel(e.target.value)
                  }}
                />
              </Filter>
              {trims?.length > 0 && (
                <Filter disabled={!make || !model}>
                  <SelectInput
                    name="Trim"
                    label="Trim"
                    value={trim}
                    options={trims}
                    onChange={(e) => {
                      onSelectTrim(e.target.value)
                    }}
                  />
                </Filter>
              )}
              <Filter
                disabled={
                  trims?.length > 0 ? !make || !model || !trim : !make || !model
                }
              >
                <SelectInput
                  name="Year"
                  label="Year"
                  value={year}
                  options={years}
                  onChange={(e) => {
                    setYear(e.target.value)
                  }}
                />
              </Filter>
              <button
                disabled={make === "" || model === "" || year === ""}
                onClick={setCar}
              >
                Build
              </button>
            </Filters>
          </Content>
        </Body>
        <Footer>
          <IconWrapper>
            <Info />
          </IconWrapper>
          Don't see your vehicle? Give us a call 888-460-0640
        </Footer>
      </Container>
    </>
  )
}

export default PowersportsCarSelector
